import type { LookupItem } from '../../../mocks/criminalMasterData';
import { isOtherLookup } from '../criminal/criminalConstants';
import { maxCharLimitExceededError } from '../../../utils/caseFieldValidation';
import { courtTypeQueryParam } from '../../../utils/courtTypeLookup';

export function requiresCourtType(hasActionInstituted: string): boolean {
  return hasActionInstituted === 'yes';
}

export { courtTypeQueryParam };

export function isHighUrgencyLevel(name: string): boolean {
  return name.trim().toLowerCase().includes('high');
}

export function isOtherNextStep(name: string, codeOrId?: string): boolean {
  return isOtherLookup(name, codeOrId);
}

/** Which step labels to show in the AG step bar (sliding window). */
export function attorneyGeneralStepShowsLabel(
  stepNumber: number,
  currentStep: number,
  totalSteps: number,
): boolean {
  if (currentStep <= 1) return stepNumber === 1 || stepNumber === 2;
  if (currentStep >= totalSteps) return stepNumber === totalSteps - 1 || stepNumber === totalSteps;
  return stepNumber >= currentStep - 1 && stepNumber <= currentStep + 1;
}

const NBSP = '\u00A0';

/** Split a step title across exactly two lines at a word boundary. */
export function splitStepLabelTwoLines(label: string): { line1: string; line2: string } {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return { line1: '', line2: NBSP };
  if (words.length === 1) return { line1: words[0], line2: NBSP };

  let bestIdx = 1;
  let bestScore = Infinity;
  for (let i = 1; i < words.length; i++) {
    const left = words.slice(0, i).join(' ');
    const right = words.slice(i).join(' ');
    const diff = Math.abs(left.length - right.length);
    const score = diff + (right.length > left.length * 1.4 ? 4 : 0);
    if (score < bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }

  const line1 = words.slice(0, bestIdx).join(' ');
  const line2 = words.slice(bestIdx).join(' ') || NBSP;
  return { line1, line2 };
}

export function isStatusOther(statusId: string, statuses: LookupItem[]): boolean {
  const row = statuses.find((s) => s.id === statusId);
  return row ? isOtherLookup(row.name, row.id) : statusId === 'other';
}

const CUSTODIAL_AUTHORITY_STATUS_CODES = new Set(['IN_SL_CUSTODY', 'IN_FOREIGN_CUSTODY']);

export function requiresCustodialAuthority(statusName: string, statusCode?: string): boolean {
  const code = (statusCode ?? '').trim().toUpperCase();
  if (code && CUSTODIAL_AUTHORITY_STATUS_CODES.has(code)) return true;
  const n = statusName.trim().toLowerCase();
  return n.includes('sl custody') || n.includes('foreign custody');
}

export function preferredAccessLabels(
  ids: string[],
  options: { id: string; name: string }[],
): string[] {
  return ids.map((id) => options.find((o) => o.id === id)?.name ?? '').filter(Boolean);
}

export const MAX_NUMBER_OF_RESPONDENTS_DIGITS = 10;

export function onlyDigitsMax(value: string, maxDigits: number): string {
  return value.replace(/[^\d]/g, '').slice(0, maxDigits);
}

export function numberOfRespondentsError(value: string): string | undefined {
  const digits = value.trim();
  if (!digits) return undefined;
  if (!/^\d+$/.test(digits)) return 'Number of respondents must contain digits only.';
  if (digits.length > MAX_NUMBER_OF_RESPONDENTS_DIGITS) return maxCharLimitExceededError();
  return undefined;
}
